
import Vue, { PropType } from 'vue'
import QRCode from 'qrcode'
import { getInstance as getAuthInstance } from '@/auth/authWrapper'
import { createLeadInAmoCrm } from '@/api/amocrm'
import { sendMassMail } from '@/api/mailing'
import {
    getSlots,
    getSmsGateQr,
    getSmsGateStatus,
    setChannelPhone,
    setChannelSim,
} from '@/api/networkFixer'
import { CHANNEL_TYPES, PROVIDER_TYPES } from '@/vars/general'
import { findFreeSmsPromoSlotId } from '@/helpers/channels/channelSlots'
import { smsPromoAllowsIntegration } from '@/helpers/promo/availableActivationTypes'
import { hasSmsSavedNumber, isSmsGateStatusAuthorized, resolveSmsWizardStep } from '@/helpers/channels/smsGateStatus'
import { getMultichatChannelDef } from '@/helpers/gcb2/multichat/channelDefs'
import { ensureMultichatDhConnector } from '@/helpers/gcb2/multichat/ensureDhConnector'
import { findActiveSmsPromo, findFreeSmsSlotId, isSmsDiscountActivation } from '@/helpers/gcb2/multichat/smsPromoDock'
import type { MultichatBootstrap, MultichatBootstrapChannel, MultichatMainView } from '@/helpers/gcb2/multichat/types'
import { findPendingSmsPromo } from '@/helpers/promo/parsePromoBundle'
import phoneValidator, { isValidPhone } from '@/helpers/validators/phoneValidator'
import config from '@root/config'
import { logSmsPromoButton } from '@/helpers/ymLogger'

const smsLogo = require('@/assets/img/messenger-logo/sms-logo.png')
const MARKETPLACE_FORM_STORAGE_KEY = 'marketplaceForm'
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const NAME_RE = /^[A-Za-zА-Яа-яЁё]+(?: [A-Za-zА-Яа-яЁё]+)*$/
const SETUP_POLL_MS = 3000

type StoredForm = { name?: string; phone?: string; email?: string }
type SimCard = { simNumber: number; carrierName?: string; slotIndex?: number }

function normalizeName(name: string): string {
    return name.replace(/\s+/g, ' ').trim()
}

function isNameValidValue(name: string): boolean {
    const normalized = normalizeName(name)
    return normalized.length >= 2 && normalized.length <= 50 && NAME_RE.test(normalized)
}

function phoneMaskFor(phone: string): string {
    const clean = phone?.startsWith('+') ? phone : `+${phone || ''}`
    if (clean.startsWith('+7') || clean.startsWith('+8')) return '+# (###) ###-##-#*'
    if (clean.startsWith('+375')) return '+### ## ###-##-#*'
    if (clean.startsWith('+380')) return '+### ## #*'
    return '+#*'
}

function digits(value: unknown): string {
    return String(value ?? '').replace(/\D/g, '')
}

function apkDownloadUrl(): string {
    const isProd = typeof window !== 'undefined' && window.location.hostname.includes('dataheroes')
    return isProd
        ? 'https://storage.yandexcloud.net/dataheroes-sms-app/latest.apk'
        : 'https://storage.yandexcloud.net/legendafiles/dh-sms-app/latest.apk'
}

export default Vue.extend({
    name: 'MultichatSmsPromoPanel',
    props: {
        bootstrap: { type: Object as PropType<MultichatBootstrap | null>, default: null },
        mainView: { type: String as PropType<MultichatMainView>, required: true },
        activating: { type: Boolean, default: false },
        busy: { type: Boolean, default: false },
    },
    data: () => ({
        smsLogo,
        apkUrl: apkDownloadUrl(),
        apkQrSrc: '',
        pairQrSrc: '',
        pairQrError: '',
        connectorId: null as string | null,
        setupStep: 0,
        setupFinished: false,
        setupReady: false,
        smsAuthorized: false,
        setupBusy: false,
        setupError: '',
        simCards: [] as SimCard[],
        simPhones: {} as Record<string, string>,
        statusPhone: '',
        simNumber: null as number | null,
        phoneInput: '',
        phoneError: '',
        formattedPhone: '',
        testPhoneInput: '',
        testPhoneError: '',
        testSent: false,
        testFailed: false,
        testFailures: 0,
        postSetup: null as null | 'tariffs',
        wizardPrepared: false,
        dhChatActivationSent: false,
        statusPollTimer: null as ReturnType<typeof setInterval> | null,
        crmStep: 'offer' as 'offer' | 'form',
        crmSubmitting: false,
        crmSubmittedLocally: false,
        phoneMaskOptions: '+# (###) ###-##-#*',
        crmForm: { name: '', phone: '', email: '' },
        crmErrors: { name: '', phone: '', email: '' },
        permissionItems: [
            { title: 'Телефон и SMS', copy: 'Разрешите доступ.' },
            { title: 'Уведомления', copy: 'Включите уведомления приложения.' },
            { title: 'Работа в фоне', copy: 'Разрешите и отключите приостановку приложения.' },
            { title: 'Батарея', copy: 'Выберите «Без ограничений».' },
            { title: 'Автозапуск', copy: 'Включите, если этот пункт есть на телефоне.' },
        ],
        setupStepLabels: [
            'Установка',
            'QR-код',
            'Номер',
            'Права',
            'Проверка',
        ],
    }),
    computed: {
        showFlowLoader(): boolean {
            if (this.busy || this.activating || this.crmSubmitting) return true
            return (
                this.mainView === 'sms_setup' &&
                !this.postSetup &&
                (!this.setupReady || this.setupBusy)
            )
        },
        canManage(): boolean {
            return Boolean(this.bootstrap?.permissions?.manageChannels)
        },
        pendingSms() {
            const list = this.bootstrap?.pendingPromos?.length
                ? this.bootstrap.pendingPromos
                : this.bootstrap?.pendingPromo
                  ? [this.bootstrap.pendingPromo]
                  : []
            return findPendingSmsPromo(list)
        },
        activeSms() {
            return findActiveSmsPromo(this.bootstrap?.activePromos)
        },
        giftDays(): number {
            return this.pendingSms?.durationDays || this.activeSms?.daysLeft || 30
        },
        promoAlreadyActive(): boolean {
            return Boolean(this.activeSms && !isSmsDiscountActivation(this.activeSms.activationType))
        },
        smsIntegrationOffer(): boolean {
            return smsPromoAllowsIntegration(this.pendingSms, Boolean(this.bootstrap?.sms?.exists))
        },
        hasFreeConnectSlot(): boolean {
            return (this.bootstrap?.slots?.freeModuleCount || 0) > 0
        },
        crmAlreadySubmitted(): boolean {
            return (
                this.crmSubmittedLocally ||
                isSmsDiscountActivation(this.activeSms?.activationType)
            )
        },
        channelHasSavedNumber(): boolean {
            return hasSmsSavedNumber({
                phone: this.statusPhone,
                simPhones: this.simPhones,
                extras: [this.formattedPhone, this.connectorPhone],
            })
        },
        chosenSimNumber(): number | null {
            const cards = this.simCards || []
            const current = this.simNumber
            const listed =
                current != null &&
                cards.some((sim) => Number(sim.simNumber) === Number(current))
            if (listed) return Number(current)
            if (cards.length > 1 && cards[0]?.simNumber != null) return Number(cards[0].simNumber)
            return current
        },
        connectorPhone(): string {
            const id = this.connectorId
            if (!id) return ''
            const connector = this.$store.getters['connectorById']?.(id)
            return String(connector?.phone || '')
        },
        showFlowTitle(): boolean {
            return this.mainView !== 'sms_setup' || Boolean(this.postSetup)
        },
        kicker(): string {
            if (this.postSetup === 'tariffs') return 'SMS DH · Рекомендации'
            if (this.mainView === 'sms_setup') {
                if (this.setupFinished) return 'SMS DH'
                return `SMS DH · шаг ${this.setupStep + 1} из 5`
            }
            if (this.mainView === 'sms_crm_offer') {
                if (this.crmAlreadySubmitted) return 'Диагностика Data Heroes'
                if (this.crmStep === 'form') return 'Диагностика Data Heroes'
            }
            return 'SMS DH'
        },
        title(): string {
            if (this.postSetup === 'tariffs') return 'Выберите пакет SMS'
            if (this.mainView === 'sms_setup') return this.setupHeading
            if (this.mainView === 'sms_crm_offer') {
                if (this.crmAlreadySubmitted) return 'Заявка отправлена'
                return this.crmStep === 'form'
                    ? 'Записаться на диагностику'
                    : 'Дарим 10% на внедрение CRM-маркетинга'
            }
            if (this.promoAlreadyActive) return 'Ваш SMS-канал в подарок'
            if (this.hasFreeConnectSlot) return 'Подключите SMS DH к Мультичату'
            return `Дарим SMS-канал на ${this.giftDays} дней`
        },
        lead(): string {
            if (this.postSetup === 'tariffs') return ''
            if (this.mainView === 'sms_setup') return this.setupLead
            if (this.mainView === 'sms_crm_offer') {
                if (this.crmAlreadySubmitted) {
                    return 'Менеджер свяжется с вами в течение рабочего дня и ответит на все вопросы.'
                }
                return this.crmStep === 'form'
                    ? 'Оставьте контакты — согласуем время и обсудим ваши задачи.'
                    : 'У вас уже подключён SMS-канал. Поэтому дарим скидку на внедрение и приглашаем вас на диагностику.'
            }
            return 'Напоминайте о записи и отправляйте сообщения клиентам с рабочего номера.'
        },
        entitlementTitle(): string {
            if (this.hasFreeConnectSlot && !this.promoAlreadyActive) {
                return 'Для SMS DH уже есть свободный канал'
            }
            return 'Дополнительный канал бесплатно'
        },
        entitlementCopy(): string {
            if (this.hasFreeConnectSlot && !this.promoAlreadyActive) {
                return 'Дополнительная плата Data Heroes не потребуется.'
            }
            return 'Сверх тарифа, без замены ваших мессенджеров.'
        },
        giftCondition(): string {
            if (this.promoAlreadyActive) {
                return 'Подарок уже активирован. Продолжите настройку телефона.'
            }
            if (this.smsIntegrationOffer) {
                return 'Подарок активируется после нажатия «Активировать».'
            }
            if (this.hasFreeConnectSlot) {
                return 'Свободный канал подписки — без доплаты Data Heroes.'
            }
            return 'Подключение без активации подарка.'
        },
        promoPrimaryLabel(): string {
            if (this.promoAlreadyActive) return 'Продолжить'
            if (this.smsIntegrationOffer) return 'Активировать'
            if (this.hasFreeConnectSlot) return 'Подключить SMS DH'
            return 'Настроить'
        },
        setupHeading(): string {
            return [
                'Установите SMS DH',
                'Отсканируйте QR-код',
                this.simCards.length > 1 ? 'Выберите SIM и укажите номер' : 'Укажите номер SIM-карты',
                'Проверьте, выданы ли приложению все права',
                'Отправьте тестовое SMS',
            ][this.setupStep]
        },
        setupLead(): string {
            return [
                'На рабочий телефон с SIM-картой.',
                `Свяжите телефон с проектом.`,
                this.simCards.length > 1
                    ? 'Выберите SIM с пакетом SMS и укажите её номер.'
                    : 'В телефоне одна SIM-карта. Укажите её номер.',
                'Настройки Android → Приложения → SMS DH.',
                '',
            ][this.setupStep]
        },
        setupPrimaryLabel(): string {
            return [
                'Приложение установлено',
                'Проверить подключение',
                'Сохранить и продолжить',
                'Выдано',
                'Отправить тестовое SMS',
            ][this.setupStep]
        },
        isEmailLocked(): boolean {
            return Boolean(this.authEmail())
        },
        isCrmFormValid(): boolean {
            return (
                isNameValidValue(this.crmForm.name) &&
                !phoneValidator(this.crmForm.phone) &&
                EMAIL_RE.test(this.crmForm.email.trim())
            )
        },
    },
    watch: {
        mainView: {
            immediate: true,
            handler(view: MultichatMainView) {
                if (view === 'sms_setup') this.prepareSetup()
                else this.stopStatusPoll()
                if (view === 'sms_crm_offer' && !this.crmAlreadySubmitted) {
                    this.crmStep = 'offer'
                }
            },
        },
    },
    beforeDestroy() {
        this.stopStatusPoll()
    },
    methods: {
        finishSetup(destination: 'channels' | 'sms-channel') {
            // Кнопка «Завершить подключение»
            logSmsPromoButton('dh_sms_finish_setup')
            this.$emit('setup-done', {
                destination,
                connectorId: this.connectorId,
            })
        },
        onLaterClick() {
            // Кнопка «Позже»
            logSmsPromoButton('dh_sms_promo_later')
            this.$emit('later')
        },
        onPrimaryClick() {
            if (this.smsIntegrationOffer) {
                // Кнопка «Активировать»
                logSmsPromoButton('dh_sms_promo_activate')
                this.$emit('activate')
                return
            }
            this.$emit('setup')
        },
        authEmail(): string {
            const auth = getAuthInstance()
            if (!auth?.isAuthenticated) return ''
            return String(auth.user?.email || '')
        },
        async prepareSetup() {
            if (this.wizardPrepared) return
            this.setupReady = false
            this.setupError = ''
            this.connectorId = this.bootstrap?.sms?.connectorId || this.connectorId
            try {
                await this.renderApkQr()
                await this.ensureSmsConnector()
                if (this.connectorId) {
                    await this.loadPairQr()
                    await this.refreshSmsStatus()
                    this.startStatusPoll()
                } else {
                    this.smsAuthorized = false
                }
                this.applySetupEntryStep()
                this.wizardPrepared = true
            } finally {
                this.setupReady = true
            }
        },
        applySetupEntryStep() {
            const step = resolveSmsWizardStep(this.smsAuthorized, this.channelHasSavedNumber)
            this.setupFinished = step === null
            if (step !== null) this.setupStep = step
            this.publishSmsStatus()
        },
        async renderApkQr() {
            try {
                this.apkQrSrc = await QRCode.toDataURL(this.apkUrl, {
                    margin: 1,
                    width: 220,
                    errorCorrectionLevel: 'M',
                })
            } catch {
                this.apkQrSrc = ''
            }
        },
        async ensureSmsConnector() {
            if (this.connectorId) return
            const projectId = this.bootstrap?.projectId
            if (!projectId) return
            const giftPromo =
                this.smsIntegrationOffer ||
                Boolean(this.activeSms && !isSmsDiscountActivation(this.activeSms.activationType))
            const slotId = giftPromo
                ? await this.waitForSmsPromoSlot(projectId)
                : await this.resolveRegularSmsSlot(projectId)
            const def = getMultichatChannelDef('sms')
            const channel: MultichatBootstrapChannel = {
                key: 'sms',
                connectorType: def?.connectorType || CHANNEL_TYPES.smsChannel,
                providerType: def?.dhProviderType || PROVIDER_TYPES.smsGate,
                entitled: true,
                paid: true,
                provider: 'dh',
                status: 'connect',
                displayName: def?.displayName || 'SMS',
                connectorId: this.bootstrap?.sms?.connectorId || null,
            }
            const result = await ensureMultichatDhConnector({
                store: this.$store,
                projectId,
                channel,
                slotId,
            })
            if (!result.ok || !result.connectorId) {
                this.setupError = result.message || 'Не удалось создать SMS-канал'
                return
            }
            this.connectorId = result.connectorId
            this.$emit('connector-created', result.connectorId)
        },
        async resolveRegularSmsSlot(projectId: string) {
            const { data: slots } = await getSlots({ projectId })
            return findFreeSmsSlotId(slots || []) || this.bootstrap?.slots?.firstFreeModuleId || null
        },
        async waitForSmsPromoSlot(projectId: string) {
            for (let attempt = 0; attempt < 3; attempt += 1) {
                const { data, error } = await getSlots({ projectId })
                if (!error) {
                    const slotId = findFreeSmsPromoSlotId(Array.isArray(data) ? data : [])
                    if (slotId) return slotId
                }
                if (attempt < 2) await new Promise((resolve) => setTimeout(resolve, 400))
            }
            return null
        },
        async loadPairQr() {
            if (!this.connectorId) return
            this.pairQrError = ''
            try {
                const res = await getSmsGateQr({ connectorId: this.connectorId })
                const payload = res?.data
                if (!payload || res?.error || !payload.url || !payload.token) {
                    this.pairQrError = 'Не удалось получить QR. Обновите шаг.'
                    this.pairQrSrc = ''
                    return
                }
                this.pairQrSrc = await QRCode.toDataURL(
                    JSON.stringify({ url: payload.url, token: payload.token }),
                    { margin: 2, width: 240, errorCorrectionLevel: 'M' }
                )
            } catch {
                this.pairQrError = 'Не удалось получить QR. Обновите шаг.'
                this.pairQrSrc = ''
            }
        },
        startStatusPoll() {
            this.stopStatusPoll()
            this.statusPollTimer = setInterval(() => {
                this.refreshSmsStatus()
            }, SETUP_POLL_MS)
        },
        stopStatusPoll() {
            if (this.statusPollTimer) {
                clearInterval(this.statusPollTimer)
                this.statusPollTimer = null
            }
        },
        async refreshSmsStatus() {
            if (!this.connectorId) return
            const wasAuthorized = this.smsAuthorized
            const status = await getSmsGateStatus({ connectorId: this.connectorId })
            this.smsAuthorized = isSmsGateStatusAuthorized(status)
            if (!this.smsAuthorized) {
                if (wasAuthorized && this.setupReady) {
                    this.setupFinished = false
                    this.setupStep = 0
                }
                return
            }
            const payload = status?.data || {}
            this.simCards = Array.isArray(payload.simCards) ? payload.simCards : []
            this.simPhones = payload.simPhones || {}
            if (payload.simNumber != null && payload.simNumber !== '') {
                this.simNumber = Number(payload.simNumber)
            }
            this.ensureDefaultSim()
            this.statusPhone = String(payload.phone || '')
            const simKey = this.simNumber != null ? String(this.simNumber) : ''
            const saved = [this.statusPhone, this.simPhones[simKey]].find((value) =>
                hasSmsSavedNumber({ phone: value })
            )
            this.formattedPhone = saved ? String(saved) : ''
            if (this.formattedPhone && !this.phoneInput) this.phoneInput = this.formattedPhone
            if (this.smsAuthorized && !this.dhChatActivationSent) {
                this.dhChatActivationSent = true
                this.$emit('activate-dh-chat')
            }
            if (this.setupReady) this.syncWizardFromStatus()
        },
        syncWizardFromStatus() {
            const step = resolveSmsWizardStep(this.smsAuthorized, this.channelHasSavedNumber)
            if (step !== null && !this.setupFinished && this.setupStep < step) {
                this.setupStep = step
            }
            this.publishSmsStatus()
        },
        isSimChosen(simNumber: number) {
            const chosen = this.chosenSimNumber
            if (chosen == null) return false
            return Number(simNumber) === Number(chosen)
        },
        ensureDefaultSim() {
            const cards = this.simCards || []
            if (cards.length < 2) return
            const current = this.simNumber
            const listed =
                current != null &&
                cards.some((sim) => Number(sim.simNumber) === Number(current))
            if (listed) return
            const first = Number(cards[0].simNumber)
            if (!Number.isFinite(first)) return
            this.simNumber = first
            if (!this.connectorId) return
            setChannelSim({ connectorId: this.connectorId, simNumber: first }).catch(() => {})
        },
        publishSmsStatus() {
            this.$emit('sms-status', {
                connected: this.smsAuthorized,
                ready: Boolean(this.smsAuthorized && this.channelHasSavedNumber),
            })
        },
        onSetupBack() {
            if (this.setupFinished || this.setupStep === 0) {
                this.$emit('later')
                return
            }
            this.setupStep -= 1
            this.setupError = ''
        },
        async onSetupPrimary() {
            if (this.setupBusy) return
            this.setupBusy = true
            this.setupError = ''
            try {
                if (this.setupStep === 1) {
                    await this.refreshSmsStatus()
                    if (!this.connectorId) {
                        this.setupError = 'Сначала создайте канал и отсканируйте QR-код.'
                        return
                    }
                    const status = await getSmsGateStatus({ connectorId: this.connectorId })
                    if (!isSmsGateStatusAuthorized(status)) {
                        this.setupError = 'Телефон ещё не подключён. Отсканируйте QR-код в приложении.'
                        return
                    }
                }
                if (this.setupStep === 2) {
                    if (!isValidPhone(this.phoneInput)) {
                        this.phoneError = phoneValidator(this.phoneInput) || 'Введите корректный номер'
                        return
                    }
                    if (this.connectorId) {
                        const res = await setChannelPhone({
                            connectorId: this.connectorId,
                            phone: this.phoneInput,
                        })
                        if (res?.error) {
                            this.phoneError = 'Не удалось сохранить номер. Попробуйте ещё раз.'
                            return
                        }
                        this.formattedPhone = this.phoneInput
                    }
                }
                if (this.setupStep === 4) {
                    await this.sendTestCheck()
                    return
                }
                this.setupStep = Math.min(4, this.setupStep + 1)
            } finally {
                this.setupBusy = false
            }
        },
        async onSelectSim(simNumber: number) {
            this.simNumber = simNumber
            if (!this.connectorId || this.setupBusy) return
            this.setupBusy = true
            try {
                await setChannelSim({ connectorId: this.connectorId, simNumber })
            } finally {
                this.setupBusy = false
            }
        },
        onPhoneMaskInput(event: Event) {
            this.phoneInput = (event.target as HTMLInputElement).value.replace('+8', '+7')
            this.phoneMaskOptions = phoneMaskFor(this.phoneInput.replace(/[^\d+]/g, ''))
            this.phoneError = ''
        },
        onTestPhoneInput(event: Event) {
            this.testPhoneInput = (event.target as HTMLInputElement).value.replace('+8', '+7')
            this.phoneMaskOptions = phoneMaskFor(this.testPhoneInput.replace(/[^\d+]/g, ''))
            this.testPhoneError = ''
        },
        async sendTestCheck() {
            this.testPhoneError = ''
            this.setupError = ''
            if (!isValidPhone(this.testPhoneInput)) {
                this.testPhoneError = phoneValidator(this.testPhoneInput) || 'Введите номер: +7 и 10 цифр.'
                return
            }
            const workPhone = this.formattedPhone || this.statusPhone || this.connectorPhone
            if (digits(this.testPhoneInput) === digits(workPhone)) {
                this.testPhoneError = 'Для проверки укажите другой номер, не рабочую SIM.'
                return
            }
            const connector = this.connectorId
                ? this.$store.getters['connectorById']?.(this.connectorId)
                : null
            const channelId = connector?.channelId
            const projectId = this.bootstrap?.projectId
            if (!this.connectorId || !projectId || channelId == null) {
                this.setupError = 'Не удалось отправить тестовое SMS. Попробуйте ещё раз.'
                return
            }
            const ownBusy = !this.setupBusy
            if (ownBusy) this.setupBusy = true
            this.testFailed = false
            this.testSent = false
            try {
                const from = digits(workPhone)
                const to = digits(this.testPhoneInput)
                const { error } = await sendMassMail({
                    projectId: String(projectId),
                    connectorId: this.connectorId,
                    service: 'cascade',
                    sendingSettings: {
                        type: 'channel',
                        id: channelId,
                    },
                    messages: [
                        {
                            from,
                            to,
                            text: 'Тестовое сообщение',
                            communicationId: null,
                            sendingSettings: {
                                type: 'channel',
                                id: channelId,
                            },
                        },
                    ],
                })
                if (error) {
                    this.setupError = 'Не удалось отправить тестовое SMS. Попробуйте ещё раз.'
                    return
                }
                this.testSent = true
            } catch {
                this.setupError = 'Не удалось отправить тестовое SMS. Попробуйте ещё раз.'
            } finally {
                if (ownBusy) this.setupBusy = false
            }
        },
        markTestFailed() {
            this.testSent = false
            this.testFailed = true
            this.testFailures += 1
        },
        openCrmForm() {
            this.hydrateCrmForm()
            this.crmStep = 'form'
        },
        hydrateCrmForm() {
            let stored: StoredForm = {}
            try {
                stored = JSON.parse(localStorage.getItem(MARKETPLACE_FORM_STORAGE_KEY) || '{}')
            } catch {
                stored = {}
            }
            this.crmForm.name = stored.name || ''
            this.crmForm.phone = stored.phone || ''
            this.crmForm.email = this.authEmail() || stored.email || ''
            this.phoneMaskOptions = phoneMaskFor(this.crmForm.phone.replace(/[^\d+]/g, ''))
            this.crmErrors = { name: '', phone: '', email: '' }
        },
        saveCrmForm() {
            localStorage.setItem(
                MARKETPLACE_FORM_STORAGE_KEY,
                JSON.stringify({
                    name: normalizeName(this.crmForm.name),
                    phone: this.crmForm.phone.replace(/[^\d+]/g, ''),
                    email: this.crmForm.email.trim(),
                })
            )
        },
        onCrmNameInput(event: Event) {
            this.crmForm.name = (event.target as HTMLInputElement).value
                .replace(/[^A-Za-zА-Яа-яЁё\s]/g, '')
                .replace(/\s+/g, ' ')
                .replace(/^\s/, '')
                .slice(0, 50)
            this.crmErrors.name = ''
        },
        onCrmPhoneInput(event: Event) {
            this.crmForm.phone = (event.target as HTMLInputElement).value.replace('+8', '+7')
            this.phoneMaskOptions = phoneMaskFor(this.crmForm.phone.replace(/[^\d+]/g, ''))
            this.crmErrors.phone = ''
        },
        validateCrmName(): boolean {
            const name = normalizeName(this.crmForm.name)
            if (!name) this.crmErrors.name = 'Поле должно быть заполнено'
            else if (!isNameValidValue(name)) {
                this.crmErrors.name =
                    'Имя должно содержать только буквы и пробелы, от 2 до 50 символов'
            } else this.crmErrors.name = ''
            return !this.crmErrors.name
        },
        validateCrmPhone(): boolean {
            this.crmErrors.phone = phoneValidator(this.crmForm.phone) || ''
            return !this.crmErrors.phone
        },
        validateCrmEmail(): boolean {
            const email = this.crmForm.email.trim()
            if (!email) this.crmErrors.email = 'Поле должно быть заполнено'
            else if (!EMAIL_RE.test(email)) this.crmErrors.email = 'Некорректный e-mail'
            else this.crmErrors.email = ''
            return !this.crmErrors.email
        },
        getUtmMarks(): Record<string, string> {
            const utmMarks: Record<string, string> = {}
            const add = (key: string, value?: string | (string | null)[] | null) => {
                if (!key.startsWith('utm_')) return
                const normalized = Array.isArray(value) ? value.find(Boolean) : value
                if (!normalized) return
                utmMarks[key] = String(normalized)
            }
            Object.entries(this.$route?.query || {}).forEach(([key, value]) =>
                add(key, value as string)
            )
            try {
                for (let i = 0; i < localStorage.length; i += 1) {
                    const key = localStorage.key(i)
                    if (!key?.startsWith('utm_') || utmMarks[key]) continue
                    const value = localStorage.getItem(key)
                    if (value) utmMarks[key] = value
                }
            } catch {
                // ignore
            }
            return utmMarks
        },
        async submitCrmForm() {
            if (this.crmSubmitting || this.crmAlreadySubmitted) return
            if (!(this.validateCrmName() && this.validateCrmPhone() && this.validateCrmEmail())) {
                return
            }
            this.crmSubmitting = true
            const integrationId = String(
                config.marketplaceModulesIds?.[config.buildType]?.crmSettings || ''
            )
            const projectId = String(this.bootstrap?.projectId || this.$route?.params?.id || '')
            try {
                this.saveCrmForm()
                await createLeadInAmoCrm({
                    name: normalizeName(this.crmForm.name),
                    phone: this.crmForm.phone.replace(/[^\d+]/g, ''),
                    email: this.crmForm.email.trim(),
                    type: 'Агентство (лиды)',
                    note: null,
                    column: 'Новая сделка',
                    requestName: 'Диагностика по промо',
                    formId: 'form0069',
                    ...(integrationId ? { integrationId } : {}),
                    ...(projectId ? { projectId } : {}),
                    utm: this.getUtmMarks(),
                    url: typeof window !== 'undefined' ? window.location.href : '',
                })
                this.crmSubmittedLocally = true
                this.$emit('crm-submitted')
            } catch {
                this.$store.dispatch('callNotify', 'Ошибка при отправке заявки. Попробуйте позже.')
            } finally {
                this.crmSubmitting = false
            }
        },
    },
})
